import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { useQueryManager } from "@/composables/useQueryManager";
import { useSignupStore } from "@/signup/signup";
import type { Company } from "@/types";
import { filter } from "lodash-es";
import { storeToRefs } from "pinia";
import { computed, toValue, type MaybeRefOrGetter, type Ref } from "vue";
import { useRoute } from "vue-router";

type QueryUpdate = Record<string, string | undefined>;

/**
 * How a per-country signup flow picks the company it works on. The URL names it (`company_id`, and
 * the device in `generator_id`), so every switch rewrites both ids together: a new owner starts with
 * neither, and another company starts without the previous company's device. Leaving one behind
 * made the next step show, overwrite or attach a device to the wrong company.
 */
export function useSignupCompanySelection(
  country: MaybeRefOrGetter<string>,
  isLoading: Ref<boolean>
) {
  const signupStore = useSignupStore();
  const { account } = storeToRefs(signupStore);
  const { updateQuery } = useQueryManager();
  const { globalErrorHandler } = useGlobalHandlers();
  const route = useRoute();

  /** The account's saved companies in this country that have started signing, so they can be continued. */
  const existingCompanies = computed<Company[]>(() =>
    filter(
      account.value.companies,
      (company) =>
        company.id != null &&
        company.country === toValue(country) &&
        company.documents.length > 0
    )
  );

  /** Starts a draft for an owner not registered yet; `query` carries the flow's own choices. */
  async function startNewCompany(query: QueryUpdate = {}): Promise<void> {
    await updateQuery({
      company_id: undefined,
      generator_id: undefined,
      ...query,
    });
  }

  /**
   * The company the URL names when it is this account's, in this country, with nothing signed yet:
   * the one this flow is signing up. One that has started signing is offered by `existingCompanies`.
   */
  const unsignedCompanyInUrl = computed(() =>
    account.value.companies.find(
      (company) =>
        company.id != null &&
        String(company.id) === route.query.company_id &&
        company.country === toValue(country) &&
        company.documents.length === 0
    )
  );

  /**
   * Picks the flow's path on its first step. A company the flow saved, or a "continue registration"
   * link named, that has nothing signed yet stays the one being signed up: starting a new one there
   * made Back to the first step and a second pick save a second company.
   */
  async function choosePath(
    query: QueryUpdate = {},
    /** Only when the pick starts a new owner: what the previous owner left in the URL to clear. */
    newOwnerQuery: QueryUpdate = {}
  ): Promise<void> {
    if (unsignedCompanyInUrl.value) {
      await updateQuery(query);
    } else {
      await startNewCompany({ ...newOwnerQuery, ...query });
    }
  }

  /** Continues `companyId`, dropping the unsaved draft company. False when the URL could not follow, which is reported. */
  async function selectExistingCompany(
    companyId: number,
    query: QueryUpdate = {}
  ): Promise<boolean> {
    isLoading.value = true;
    try {
      await updateQuery({
        company_id: String(companyId),
        generator_id: undefined,
        ...query,
      });
      signupStore.removeEmptyCompanyIfExists();
      return true;
    } catch (error) {
      globalErrorHandler(error);
      return false;
    } finally {
      isLoading.value = false;
    }
  }

  /** Back to the first step with no company chosen. */
  async function resetCompanySelection(query: QueryUpdate = {}): Promise<void> {
    await updateQuery({
      company_id: undefined,
      generator_id: undefined,
      step: "1",
      ...query,
    });
    signupStore.removeEmptyCompanyIfExists();
  }

  return {
    existingCompanies,
    startNewCompany,
    choosePath,
    selectExistingCompany,
    resetCompanySelection,
  };
}
